import { useState, useMemo } from "react";
import {
  Target,
  Plus,
  Search,
  CheckCircle2,
  AlertTriangle,
  Clock,
  TrendingUp,
  Trash2,
  Edit2,
  Users,
} from "lucide-react";
import { useGetList, useDataProvider, useNotify, useCanAccess } from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePipelines } from "../deals/usePipelines";
import { GoalDialog } from "./GoalDialog";
import {
  calculateGoalProgress,
  formatGoalValue,
  getGoalStatusBadge,
} from "./goalUtils";
import type { Goal, Deal, Sale, GoalProgressInfo } from "../types";

export const GoalsPage = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { pipelines } = usePipelines();

  const { canAccess: isAdmin } = useCanAccess({
    action: "write",
    resource: "goals",
  });

  // Carrega metas, negócios e equipe
  const {
    data: goalsList,
    isPending: isLoadingGoals,
    refetch: refetchGoals,
  } = useGetList<Goal>("goals", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "created_at", order: "DESC" },
  });

  const { data: dealsList, isPending: isLoadingDeals } = useGetList<Deal>(
    "deals",
    {
      pagination: { page: 1, perPage: 1000 },
    },
  );

  const { data: salesList } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 200 },
  });

  const salesMap = useMemo(() => {
    const map = new Map<number, string>();
    if (salesList) {
      salesList.forEach((s) => {
        const name =
          `${s.first_name || ""} ${s.last_name || ""}`.trim() || s.email;
        map.set(Number(s.id), name);
      });
    }
    return map;
  }, [salesList]);

  // Estados da página
  const [search, setSearch] = useState("");
  const [pipelineFilter, setPipelineFilter] = useState("all");
  const [scopeFilter, setScopeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [goalToEdit, setGoalToEdit] = useState<Goal | null>(null);

  const [goalToDelete, setGoalToDelete] = useState<Goal | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Computa o progresso de cada meta
  const calculatedGoals = useMemo<GoalProgressInfo[]>(() => {
    if (!goalsList || !dealsList) return [];
    return goalsList.map((g) => {
      const salesName = g.sales_id
        ? salesMap.get(Number(g.sales_id))
        : undefined;
      return calculateGoalProgress(g, dealsList, salesName);
    });
  }, [goalsList, dealsList, salesMap]);

  // Métricas do Topo
  const kpis = useMemo(() => {
    const active = calculatedGoals.filter((g) => !g.isEnded);
    const totalActive = active.length;
    const avgPercent =
      totalActive > 0
        ? Math.round(
            active.reduce((acc, curr) => acc + curr.percent, 0) / totalActive,
          )
        : 0;
    const beatCount = calculatedGoals.filter((g) => g.percent >= 100).length;
    const atRiskCount = active.filter(
      (g) => g.status === "warning" || g.status === "late",
    ).length;

    return { totalActive, avgPercent, beatCount, atRiskCount };
  }, [calculatedGoals]);

  // Filtragem dos cards de metas
  const filteredGoals = useMemo(() => {
    return calculatedGoals.filter((item) => {
      const { goal, isEnded, percent } = item;

      // Busca por texto
      if (search.trim()) {
        const query = search.toLowerCase();
        const matchesName = goal.name.toLowerCase().includes(query);
        const matchesStage = goal.stage.toLowerCase().includes(query);
        if (!matchesName && !matchesStage) return false;
      }

      // Funil
      if (pipelineFilter !== "all" && goal.pipeline_id !== pipelineFilter) {
        return false;
      }

      // Escopo
      if (scopeFilter !== "all" && goal.scope !== scopeFilter) {
        return false;
      }

      // Status
      if (statusFilter === "active" && isEnded) return false;
      if (statusFilter === "ended" && !isEnded) return false;
      if (statusFilter === "beat" && percent < 100) return false;

      return true;
    });
  }, [calculatedGoals, search, pipelineFilter, scopeFilter, statusFilter]);

  // Handlers
  const handleOpenCreate = () => {
    setGoalToEdit(null);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (goal: Goal) => {
    setGoalToEdit(goal);
    setIsDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!goalToDelete) return;
    setIsDeleting(true);
    try {
      await dataProvider.delete("goals", {
        id: goalToDelete.id,
        previousData: goalToDelete,
      });
      notify("Meta excluída com sucesso!", { type: "success" });
      refetchGoals();
      setGoalToDelete(null);
    } catch (err: any) {
      console.error("Erro ao excluir meta:", err);
      notify("Erro ao excluir meta: " + (err.message || "Tente novamente"), {
        type: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const isLoading = isLoadingGoals || isLoadingDeals;

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12 animate-in fade-in-50 duration-300">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b pb-5">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-primary/10 text-primary">
              <Target className="w-5 h-5" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Metas Comerciais
            </h1>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Defina e monitore o atingimento de metas da equipe e individuais
            diretamente por etapa do funil.
          </p>
        </div>

        {isAdmin && (
          <Button
            type="button"
            size="sm"
            onClick={handleOpenCreate}
            className="gap-2 text-xs font-semibold shadow-xs"
          >
            <Plus className="w-4 h-4" />
            Nova Meta
          </Button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Card className="border-border shadow-xs">
          <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-xs font-medium text-muted-foreground">
              Metas Ativas
            </CardTitle>
            <Target className="w-4 h-4 text-primary" />
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold text-foreground">
              {kpis.totalActive}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Vigentes no período
            </p>
          </CardContent>
        </Card>

        <Card className="border-border shadow-xs">
          <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-xs font-medium text-muted-foreground">
              % Média Atingida
            </CardTitle>
            <TrendingUp className="w-4 h-4 text-blue-500" />
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold text-foreground">
              {kpis.avgPercent}%
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Média entre metas ativas
            </p>
          </CardContent>
        </Card>

        <Card className="border-border shadow-xs">
          <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-xs font-medium text-muted-foreground">
              Metas Batidas
            </CardTitle>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {kpis.beatCount}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              100% ou mais alcançado
            </p>
          </CardContent>
        </Card>

        <Card className="border-border shadow-xs">
          <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-xs font-medium text-muted-foreground">
              Em Atenção / Atraso
            </CardTitle>
            <AlertTriangle className="w-4 h-4 text-amber-500" />
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
              {kpis.atRiskCount}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Necessitam de tração
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Barra de Filtros */}
      <div className="flex flex-col sm:flex-row items-center gap-2.5 bg-card border border-border p-3 rounded-xl shadow-xs">
        <div className="relative flex-1 w-full">
          <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome da meta ou etapa..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 text-xs h-8.5 bg-background"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Filtro de Funil */}
          <Select value={pipelineFilter} onValueChange={setPipelineFilter}>
            <SelectTrigger className="h-8.5 text-xs w-[150px] bg-background">
              <SelectValue placeholder="Funil" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">
                Todos os funis
              </SelectItem>
              {pipelines.map((p) => (
                <SelectItem key={p.id} value={p.id} className="text-xs">
                  {p.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Filtro de Escopo */}
          <Select value={scopeFilter} onValueChange={setScopeFilter}>
            <SelectTrigger className="h-8.5 text-xs w-[140px] bg-background">
              <SelectValue placeholder="Escopo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">
                Todos os escopos
              </SelectItem>
              <SelectItem value="etapa" className="text-xs">
                Toda a equipe
              </SelectItem>
              <SelectItem value="usuario" className="text-xs">
                Por vendedor
              </SelectItem>
            </SelectContent>
          </Select>

          {/* Filtro de Status */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8.5 text-xs w-[130px] bg-background">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active" className="text-xs">
                Ativas
              </SelectItem>
              <SelectItem value="beat" className="text-xs">
                Batidas
              </SelectItem>
              <SelectItem value="ended" className="text-xs">
                Encerradas
              </SelectItem>
              <SelectItem value="all" className="text-xs">
                Todas
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Grid de Metas */}
      {isLoading ? (
        <div className="py-16 text-center text-xs text-muted-foreground flex flex-col items-center gap-2">
          <Clock className="w-5 h-5 animate-spin text-primary" />
          <span>Carregando metas comerciais...</span>
        </div>
      ) : filteredGoals.length === 0 ? (
        <div className="py-16 text-center text-xs text-muted-foreground bg-card border border-dashed rounded-xl p-8 space-y-3">
          <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto text-muted-foreground">
            <Target className="w-6 h-6" />
          </div>
          <h3 className="font-semibold text-foreground text-sm">
            Nenhuma meta encontrada
          </h3>
          <p className="max-w-md mx-auto text-muted-foreground">
            {goalsList && goalsList.length > 0
              ? "Nenhuma meta corresponde aos filtros selecionados. Experimente ajustar a busca ou os filtros."
              : "Defina a primeira meta da sua equipe para acompanhar o progresso e motivar o time de vendas."}
          </p>
          {isAdmin && (
            <div className="pt-2">
              <Button
                size="sm"
                onClick={handleOpenCreate}
                className="text-xs gap-1.5"
              >
                <Plus className="w-4 h-4" />
                Criar Primeira Meta
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredGoals.map((item) => {
            const {
              goal,
              achieved,
              remaining,
              percent,
              daysRemaining,
              status,
              isEnded,
              salesPersonName,
            } = item;
            const badge = getGoalStatusBadge(status, percent);

            const pipelineObj = pipelines.find(
              (p) => p.id === goal.pipeline_id,
            );
            const pipelineName = pipelineObj?.nome || "Funil Comercial";
            const stageObj = pipelineObj?.etapas.find(
              (s) => s.value === goal.stage,
            );
            const stageLabel = stageObj?.label || goal.stage;

            return (
              <Card
                key={goal.id}
                className="border-border hover:border-primary/40 transition-colors shadow-xs flex flex-col justify-between"
              >
                <CardHeader className="p-4 pb-2.5 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-semibold text-foreground text-sm tracking-tight leading-snug">
                        {goal.name}
                      </h3>
                      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-0.5">
                        <span className="font-medium text-foreground/80">
                          {pipelineName}
                        </span>
                        <span>•</span>
                        <span className="bg-muted px-1.5 py-0.2 rounded font-medium">
                          {stageLabel}
                        </span>
                      </div>
                    </div>

                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border shrink-0 ${badge.bgClass}`}
                    >
                      {badge.label}
                    </span>
                  </div>

                  {/* Escopo: Toda a equipe vs Vendedor */}
                  <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    <Users className="w-3 h-3" />
                    <span>
                      {goal.scope === "etapa" ? (
                        <strong>Toda a Equipe</strong>
                      ) : (
                        <span>
                          Vendedor:{" "}
                          <strong>{salesPersonName || "Atribuído"}</strong>
                        </span>
                      )}
                    </span>
                  </div>
                </CardHeader>

                <CardContent className="p-4 pt-1 space-y-3">
                  {/* Valores e Percentual */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">
                        Atingido:{" "}
                        <strong className="text-foreground">
                          {formatGoalValue(achieved, goal.metric_type)}
                        </strong>{" "}
                        de{" "}
                        {formatGoalValue(goal.target_value, goal.metric_type)}
                      </span>
                      <span className="font-bold text-foreground text-sm">
                        {percent}%
                      </span>
                    </div>

                    {/* Barra de Progresso */}
                    <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${badge.barClass}`}
                        style={{
                          width: `${Math.min(100, Math.max(0, percent))}%`,
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-0.5">
                      <span>
                        {percent >= 100
                          ? "🎉 Alvo superado!"
                          : `Faltam: ${formatGoalValue(remaining, goal.metric_type)}`}
                      </span>
                      <span>
                        {isEnded ? (
                          <span className="font-medium text-muted-foreground">
                            Encerrada
                          </span>
                        ) : (
                          `Termina em ${daysRemaining} ${daysRemaining === 1 ? "dia" : "dias"}`
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Período de Vigência e Ações */}
                  <div className="pt-2 border-t flex items-center justify-between text-[11px] text-muted-foreground">
                    <span className="truncate">
                      {new Date(
                        goal.start_date + "T00:00:00",
                      ).toLocaleDateString("pt-BR")}{" "}
                      a{" "}
                      {new Date(goal.end_date + "T00:00:00").toLocaleDateString(
                        "pt-BR",
                      )}
                    </span>

                    {/* Ações para administradores */}
                    {isAdmin && (
                      <div className="flex items-center gap-1">
                        {!isEnded && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                            onClick={() => handleOpenEdit(goal)}
                            title="Editar Meta"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => setGoalToDelete(goal)}
                          title="Excluir Meta"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal de Criação / Edição */}
      <GoalDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSuccess={() => refetchGoals()}
        goalToEdit={goalToEdit}
      />

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={Boolean(goalToDelete)}
        onOpenChange={(open) => !open && setGoalToDelete(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Excluir Meta Comercial</DialogTitle>
            <DialogDescription className="text-xs">
              Tem certeza que deseja excluir a meta{" "}
              <strong>"{goalToDelete?.name}"</strong>? Esta ação não pode ser
              desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isDeleting}
              onClick={() => setGoalToDelete(null)}
              className="text-xs"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={isDeleting}
              onClick={handleConfirmDelete}
              className="text-xs"
            >
              {isDeleting ? "Excluindo..." : "Excluir Meta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
